import GoogleAIStudioRunDialog from "../gateway/GoogleAIStudioRunDialog";
import { useMemo, useState } from "react";
import {
  Play, Loader2, AlertTriangle, CheckCircle2, Copy, Image as ImageIcon,
  Download, Upload, ServerOff,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

// Tasks the UI can dispatch through /engine-registry/{id}/test.
// Kept small on purpose — a full task registry lives in the Capability
// Router. Adding a task here only requires a matching engine capability.
const TASK_OPTIONS = [
  { id: "chat", label: "Chat (texto)" },
  { id: "image_generation", label: "Generar imagen" },
  { id: "image_edit", label: "Editar imagen" },
  { id: "vision", label: "Analizar imagen" },
];

// Heuristics to detect that the operator's VPS still lacks the
// `POST /generate_image` endpoint. Matches the exact copy the adapter
// produces (see /app/docs/VPS_IMAGE_EXTENSION.md).
const VPS_NOT_READY_MARKERS = [
  "VPS_IMAGE_EXTENSION",
  "POST /generate_image",
  "no está disponible en el VPS",
];

export default function RunEngineDialog(props) {
  if (props.engine?.adapter === "google_ai_studio_web") return <GoogleAIStudioRunDialog target={{id: props.engine.config?.web_target_id || "google-ai-studio-web", provider: "google-ai-studio"}} onClose={props.onClose} />;
  return <LegacyRunEngineDialog {...props} />;
}
function LegacyRunEngineDialog({ engine, onClose }) {
  const [task, setTask] = useState(_defaultTaskFor(engine));
  const [prompt, setPrompt] = useState("");
  const [inputImageB64, setInputImageB64] = useState("");
  const [inputImageMime, setInputImageMime] = useState("");
  const [inputImageName, setInputImageName] = useState("");
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [errorKind, setErrorKind] = useState("generic");

  const needsInputImage = task === "image_edit" || task === "vision";
  const canSend = useMemo(() => {
    if (status === "running") return false;
    if (!prompt.trim()) return false;
    if (needsInputImage && !inputImageB64) return false;
    return true;
  }, [prompt, status, needsInputImage, inputImageB64]);

  const onFilePicked = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 8 * 1024 * 1024) {
      toast.error("Selecciona PNG, JPEG o WebP de hasta 8 MiB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      // "data:image/png;base64,AAAA..." → split into mime + b64.
      const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
      if (!match) {
        toast.error("No se pudo leer la imagen.");
        return;
      }
      setInputImageMime(match[1]);
      setInputImageB64(match[2]);
      setInputImageName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const clearInputImage = () => {
    setInputImageB64("");
    setInputImageMime("");
    setInputImageName("");
  };

  const send = async () => {
    setStatus("running");
    setResult(null);
    setError("");
    setErrorKind("generic");
    try {
      const body = { prompt: prompt.trim(), task, input: {}, options: {} };
      if (needsInputImage) {
        body.input.image_b64 = inputImageB64;
        if (inputImageMime) body.input.image_mime_type = inputImageMime;
      }
      const r = await api.post(`/engine-registry/${engine.id}/test`, body);
      setResult(r.data);
      setStatus("response");
    } catch (e) {
      const msg = apiErrorMessage(e);
      if (VPS_NOT_READY_MARKERS.some((m) => msg.includes(m))) {
        setErrorKind("vps_not_ready");
      }
      setError(msg);
      setStatus("error");
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(result?.text || "");
      toast.success("Respuesta copiada");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const artifacts = (result?.artifacts || []).filter(
    (a) => a && a.type === "image" && a.data_b64,
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={TID.engines.testDialog}
        className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-4 w-4 text-emerald-400" />
            Probar «{engine.name}»
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-[11px] font-mono text-zinc-400 space-y-0.5">
          <div>engine_id: <span className="text-zinc-300">{engine.id}</span></div>
          <div>adapter: <span className="text-zinc-300">{engine.adapter}</span></div>
          <div>provider: <span className="text-zinc-300">{engine.provider}</span></div>
          {Array.isArray(engine.capabilities) && engine.capabilities.length > 0 && (
            <div>capabilities: <span className="text-zinc-300">
              {engine.capabilities.join(", ")}
            </span></div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Task</Label>
            <Select value={task} onValueChange={setTask}>
              <SelectTrigger
                data-testid="engine-test-task"
                className="bg-zinc-900 border-zinc-800"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {TASK_OPTIONS.filter((t) => engine.adapter !== "gemini_image_web" || ["image_generation", "image_edit"].includes(t.id)).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Adapter dispatch</Label>
            <div className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2.5 py-1.5 text-[11px] font-mono text-zinc-400">
              {engine.adapter}
            </div>
          </div>
        </div>

        {needsInputImage && (
          <div
            data-testid="engine-test-input-image"
            className="space-y-1.5 rounded-md border border-zinc-800 bg-zinc-950/60 p-3"
          >
            <Label className="text-xs text-zinc-400 flex items-center gap-1.5">
              <Upload className="h-3 w-3" /> Imagen de entrada (para editar)
            </Label>
            {!inputImageB64 && (
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onFilePicked}
                data-testid="engine-test-input-image-picker"
                className="text-[11px] text-zinc-400 file:mr-3 file:rounded file:border-0 file:bg-zinc-800 file:px-3 file:py-1 file:text-xs file:text-zinc-200 hover:file:bg-zinc-700"
              />
            )}
            {inputImageB64 && (
              <div className="flex items-start gap-3">
                <img
                  src={`data:${inputImageMime};base64,${inputImageB64}`}
                  alt={inputImageName}
                  className="max-w-[80px] max-h-[80px] rounded border border-zinc-800 object-cover"
                />
                <div className="flex-1 min-w-0 text-[11px] font-mono text-zinc-400">
                  <div className="truncate">{inputImageName}</div>
                  <div className="text-zinc-500">
                    {inputImageMime} · {Math.round(inputImageB64.length * 0.75 / 1024)} KB
                  </div>
                  <button
                    onClick={clearInputImage}
                    className="mt-1 text-red-300 hover:text-red-200"
                  >
                    Quitar imagen
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs text-zinc-400">Prompt</Label>
          <Textarea
            data-testid={TID.engines.testPrompt}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder={
              task === "image_generation"
                ? "Ej: A calico cat astronaut on Mars, cinematic lighting"
                : task === "image_edit"
                ? "Ej: Add a red hat and cinematic lighting"
                : "Escribe tu prompt…"
            }
            className="bg-zinc-900 border-zinc-800 text-sm resize-none"
            disabled={status === "running"}
          />
        </div>

        <div className="min-h-[120px]">
          {status === "idle" && (
            <div className="rounded-md border border-dashed border-zinc-800 p-6 text-center text-xs text-zinc-500">
              Elige un task, escribe un prompt y pulsa <b>Enviar</b>.
            </div>
          )}

          {status === "running" && (
            <div className="flex items-center gap-2 rounded-md border border-blue-900/50 bg-blue-950/20 p-3 text-xs text-blue-200">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {task === "chat"
                ? "Ejecutando motor… (rebote al VPS + Playwright, puede tardar)."
                : "Procesando imagen con el motor seleccionado…"}
            </div>
          )}

          {status === "response" && result && (
            <div
              data-testid={TID.engines.testResult}
              className="rounded-md border border-emerald-900/50 bg-emerald-950/10 p-3 space-y-3"
            >
              <div className="flex items-center justify-between text-[11px] text-emerald-300/80">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {result.adapter}
                  {typeof result.duration_ms === "number" && (
                    <span className="ml-2 text-zinc-500 font-mono">
                      {result.duration_ms} ms
                    </span>
                  )}
                </div>
                {result.text && (
                  <button
                    onClick={copyText}
                    className="text-zinc-400 hover:text-white flex items-center gap-1"
                  >
                    <Copy className="h-3 w-3" /> Copiar texto
                  </button>
                )}
              </div>

              {result.text && (
                <div className="text-sm text-zinc-200 whitespace-pre-wrap font-mono break-words">
                  {result.text}
                </div>
              )}

              {artifacts.length > 0 && (
                <div
                  data-testid="engine-test-image-artifacts"
                  className="space-y-2"
                >
                  <div className="text-[11px] font-mono text-emerald-300/80 flex items-center gap-1.5">
                    <ImageIcon className="h-3 w-3" />
                    {artifacts.length} imagen{artifacts.length > 1 ? "es" : ""} generada{artifacts.length > 1 ? "s" : ""}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {artifacts.map((a, idx) => {
                      const src = `data:${a.mime_type || "image/png"};base64,${a.data_b64}`;
                      const filename = `krai-${engine.id}-${Date.now()}-${idx}.${(a.mime_type || "image/png").split("/")[1] || "png"}`;
                      return (
                        <div
                          key={idx}
                          className="rounded-md border border-zinc-800 bg-zinc-950 overflow-hidden"
                        >
                          <img
                            src={src}
                            alt={`Generada ${idx + 1}`}
                            className="w-full h-auto max-h-[320px] object-contain bg-zinc-900"
                          />
                          <div className="flex items-center justify-between px-2 py-1 text-[10px] font-mono text-zinc-500">
                            <span>
                              {a.mime_type || "image/png"}
                              {a.width && a.height ? ` · ${a.width}×${a.height}` : ""}
                            </span>
                            <a
                              href={src}
                              download={filename}
                              className="inline-flex items-center gap-1 text-zinc-300 hover:text-white"
                            >
                              <Download className="h-3 w-3" /> Descargar
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!result.text && artifacts.length === 0 && (
                <div className="text-[11px] text-zinc-500 italic">
                  El motor completó sin texto ni artifacts.
                </div>
              )}
            </div>
          )}

          {status === "error" && errorKind === "vps_not_ready" && (
            <div
              data-testid="engine-test-vps-not-ready"
              className="rounded-md border border-amber-900/60 bg-amber-950/20 p-3 text-xs text-amber-200 space-y-2"
            >
              <div className="flex items-start gap-2 font-semibold">
                <ServerOff className="h-4 w-4 mt-0.5 shrink-0" />
                Falta aplicar la extensión de imagen en el VPS
              </div>
              <div className="text-amber-100/80">
                El adapter <code className="font-mono">gemini_image_web</code>{" "}
                requiere el endpoint <code className="font-mono">POST /generate_image</code>{" "}
                en tu VPS, y aún no responde.
              </div>
              <div className="text-amber-100/80">
                Aplica las instrucciones del documento:
                <div className="mt-1 font-mono text-[11px] rounded bg-black/40 px-2 py-1 text-amber-200">
                  /app/docs/VPS_IMAGE_EXTENSION.md
                </div>
              </div>
              <div className="text-[10px] text-amber-300/70 italic">
                Cuando el VPS esté listo, vuelve a pulsar «Enviar». No es necesario recrear el motor.
              </div>
            </div>
          )}

          {status === "error" && errorKind !== "vps_not_ready" && (
            <div
              data-testid={TID.engines.testError}
              className="rounded-md border border-red-900/50 bg-red-950/10 p-3 text-xs text-red-300 flex items-start gap-2"
            >
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div className="whitespace-pre-wrap break-words">{error}</div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Cerrar</Button>
          <Button
            data-testid={TID.engines.testSend}
            onClick={send}
            disabled={!canSend}
            size="sm"
            className="gap-2 bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-50"
          >
            {status === "running" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            {status === "running" ? "Ejecutando…" : "Enviar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Pick the sensible default task for the engine, based on its capabilities:
 *   - image_generation → default to "image_generation"
 *   - image_edit but no image_generation → default to "image_edit"
 *   - else → "chat"
 * Kept as a plain helper (not a hook) so it runs once during useState().
 */
function _defaultTaskFor(engine) {
  if (engine?.adapter === "gemini_image_web") return "image_edit";
  const caps = Array.isArray(engine?.capabilities) ? engine.capabilities : [];
  if (caps.includes("image_generation")) return "image_generation";
  if (caps.includes("image_edit")) return "image_edit";
  return "chat";
}

